import { useState, useCallback } from 'react';
import gamesService from '../services/gamesService';
import { ProcessedTicketData } from '../types/processedTicket';

interface UsePayoutsProps {
  storeId: string;
  storeData: any;
  setError: (error: string | null) => void;
}

export const usePayouts = ({ storeId, storeData, setError }: UsePayoutsProps) => {
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [posAmount, setPosAmount] = useState('');
  const [selectedPayoutType, setSelectedPayoutType] = useState<'lotto' | 'scratchers'>('lotto');
  const [selectedPayoutPack, setSelectedPayoutPack] = useState<ProcessedTicketData | null>(null);
  const [lotteryPayoutLoading, setLotteryPayoutLoading] = useState(false);
  const [posPayoutLoading, setPosPayoutLoading] = useState(false);
  const [scratcherPayoutLoading, setScratcherPayoutLoading] = useState(false);

  const handleLotteryPayout = useCallback(async (type: 'lotto' | 'scratchers', packs: ProcessedTicketData[]) => {
    if ((type === 'lotto' && lotteryPayoutLoading) || (type === 'scratchers' && scratcherPayoutLoading) || !packs || packs.length === 0) return;

    // Get a random pack for payout simulation
    const randomPack = packs[Math.floor(Math.random() * packs.length)];
    
    // Set the selected pack and type for the dialog
    setSelectedPayoutPack(randomPack);
    setSelectedPayoutType(type);
    setPayoutAmount('');
    setPosAmount('');
    setPayoutDialogOpen(true);
  }, [lotteryPayoutLoading, scratcherPayoutLoading]);

  const handlePosPayoutWithAmount = useCallback(async (type: 'lotto' | 'scratchers', amount: number) => {
    if (posPayoutLoading) return;

    setPosPayoutLoading(true);
    setError(null);

    try {
      // Get the actual store_key from storeData or use storeId as fallback
      const actualStoreKey = storeData?.store_key || storeId || '';

      // Prepare POS payout request
      const posPayoutRequest = {
        date_time: new Date().toISOString(),
        store_key: actualStoreKey,
        amount: amount,
        payout_type: type,
      };

      const response = await gamesService.getPosPayoutSummary(posPayoutRequest);

      if (response.success) {
        console.log('POS payout summary retrieved successfully:', response.message);
      } else {
        setError(`Failed to get POS payout summary: ${response.message}`);
        console.error('POS payout failed:', response.message);
      }
    } catch (error) {
      console.error('Error getting POS payout summary:', error);
      setError('Failed to get POS payout summary. Please try again.');
    } finally {
      setPosPayoutLoading(false);
    }
  }, [posPayoutLoading, storeData, storeId, setError]);

  const handlePayoutSubmit = useCallback(async () => {
    if (!selectedPayoutPack || !payoutAmount || parseFloat(payoutAmount) <= 0 || !posAmount || parseFloat(posAmount) <= 0) return;

    // Set the appropriate loading state based on type
    if (selectedPayoutType === 'lotto') {
      setLotteryPayoutLoading(true);
    } else {
      setScratcherPayoutLoading(true);
    }
    setError(null);

    const payoutAmountValue = parseFloat(payoutAmount);
    const posAmountValue = parseFloat(posAmount);

    try {
      // Get the actual store_key from storeData or use storeId as fallback
      const actualStoreKey = storeData?.store_key || storeId || '';

      // Prepare payout request
      const payoutRequest = {
        store_key: actualStoreKey,
        date_time: new Date().toISOString(),
        payout_type: selectedPayoutType,
        amount: payoutAmountValue,
      };

      const response = await gamesService.payoutLotteryTicket(payoutRequest);

      if (response.success) {

        // Close the dialog
        setPayoutDialogOpen(false);
        setPayoutAmount('');
        setPosAmount('');
        setSelectedPayoutPack(null);
      } else {
        setError(`Failed to process lottery payout: ${response.message}`);
        console.error('Lottery payout failed:', response.message);
      }
    } catch (error) {
      console.error('Error processing lottery payout:', error);
      setError('Failed to process lottery payout. Please try again.');
    } finally {
      // Reset the appropriate loading state based on type
      if (selectedPayoutType === 'lotto') {
        setLotteryPayoutLoading(false);
      } else {
        setScratcherPayoutLoading(false);
      }
    }
    
    // Call POS payout after lottery payout with the entered POS amount
    handlePosPayoutWithAmount(selectedPayoutType, posAmountValue);
  }, [selectedPayoutPack, payoutAmount, posAmount, selectedPayoutType, storeData, storeId, setError, handlePosPayoutWithAmount]);

  const handlePayoutCancel = useCallback(() => {
    setPayoutDialogOpen(false);
    setPayoutAmount('');
    setPosAmount('');
    setSelectedPayoutPack(null);
  }, []);

  return {
    payoutDialogOpen,
    payoutAmount,
    setPayoutAmount,
    posAmount,
    setPosAmount,
    selectedPayoutType,
    selectedPayoutPack,
    lotteryPayoutLoading,
    posPayoutLoading,
    scratcherPayoutLoading,
    handleLotteryPayout,
    handlePayoutSubmit,
    handlePayoutCancel,
  };
}; 
